/**
 * AI Chat Assistant Page
 * Full-page AI chat interface under AI Hub.
 * Features:
 * - Full conversation history
 * - Multiple chat sessions
 * - Context switching (general, sales coaching, data analysis)
 * - Voice message support
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import VoiceMessageRecorder from '../components/VoiceMessageRecorder';
import { toast } from 'sonner';
import {
  MessageSquare,
  Sparkles,
  Send,
  Plus,
  Trash2,
  RefreshCw,
  Loader2,
  Clock,
  Brain,
  TrendingUp,
  HelpCircle,
  Mic
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const CONTEXT_TYPES = [
  { value: 'general', label: 'General Assistant', icon: HelpCircle, description: 'CRM help & guidance' },
  { value: 'sales_coaching', label: 'Sales Coach', icon: TrendingUp, description: 'Sales tips & strategies' },
  { value: 'data_analysis', label: 'Data Analyst', icon: Brain, description: 'Stats & insights' }
];

const AIChatAssistant = () => {
  const { token, user } = useAuth();
  const { isDarkMode } = useTheme();
  const [sessions, setSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [contextType, setContextType] = useState('general');
  const [suggestions, setSuggestions] = useState([]);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Fetch sessions on mount
  useEffect(() => {
    if (token) {
      fetchSessions();
      fetchSuggestions();
    }
  }, [token]);

  // Load messages when session changes
  useEffect(() => {
    if (currentSessionId) {
      loadSessionHistory(currentSessionId);
    }
  }, [currentSessionId]);

  const fetchSessions = async () => {
    setLoadingSessions(true);
    try {
      const response = await fetch(`${API_BASE}/api/ai-chat/sessions?token=${token}`);
      const data = await response.json();
      if (data.success) {
        setSessions(data.sessions || []);
        if (data.sessions?.length > 0 && !currentSessionId) {
          setCurrentSessionId(data.sessions[0].session_id);
        }
      }
    } catch (error) {
      console.error('Error fetching sessions:', error);
    } finally {
      setLoadingSessions(false);
    }
  };

  const fetchSuggestions = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/ai-chat/suggestions?token=${token}`);
      const data = await response.json();
      if (data.success) {
        setSuggestions(data.suggestions || []);
      }
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    }
  };

  const loadSessionHistory = async (sessionId) => {
    try {
      const response = await fetch(`${API_BASE}/api/ai-chat/sessions/${sessionId}/history?token=${token}`);
      const data = await response.json();
      if (data.success) {
        setMessages(data.history || []);
      }
    } catch (error) {
      console.error('Error loading history:', error);
    }
  };

  const createNewSession = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/ai-chat/sessions/new?token=${token}`, {
        method: 'POST'
      });
      const data = await response.json();
      if (data.success) {
        setCurrentSessionId(data.session_id);
        setMessages([]);
        setSessions(prev => [{
          session_id: data.session_id,
          last_message: 'New conversation',
          last_updated: new Date().toISOString(),
          message_count: 0
        }, ...prev]);
        toast.success('New chat started!');
      }
    } catch (error) {
      console.error('Error creating session:', error);
      toast.error('Failed to create new chat');
    }
  };

  const deleteSession = async (sessionId) => {
    try {
      const response = await fetch(`${API_BASE}/api/ai-chat/sessions/${sessionId}?token=${token}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.success) {
        setSessions(prev => prev.filter(s => s.session_id !== sessionId));
        if (currentSessionId === sessionId) {
          setCurrentSessionId(sessions[0]?.session_id || null);
          setMessages([]);
        }
        toast.success('Chat deleted');
      }
    } catch (error) {
      console.error('Error deleting session:', error);
    }
  };

  const sendMessage = async (messageText) => {
    if (!messageText.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: messageText,
      created_at: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const response = await fetch(`${API_BASE}/api/ai-chat/message?token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: messageText,
          session_id: currentSessionId,
          context_type: contextType
        })
      });

      const data = await response.json();

      if (data.success) {
        if (!currentSessionId) {
          setCurrentSessionId(data.session_id);
          fetchSessions(); // Refresh sessions list
        }

        const assistantMessage = {
          role: 'assistant',
          content: data.response,
          created_at: new Date().toISOString()
        };

        setMessages(prev => [...prev, assistantMessage]);
      } else {
        toast.error(data.error || 'Failed to get response');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Connection error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(inputValue);
    }
  };

  return (
    <div className={`p-6 min-h-screen ${isDarkMode ? 'bg-background' : 'bg-gray-50'}`}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-purple-900/50' : 'bg-purple-100'}`}>
              <Sparkles className={`h-6 w-6 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
            </div>
            <div>
              <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                AI Chat Assistant
              </h1>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Your intelligent CRM companion powered by Claude
              </p>
            </div>
          </div>
          
          {/* Context selector */}
          <Select value={contextType} onValueChange={setContextType}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Select mode" />
            </SelectTrigger>
            <SelectContent>
              {CONTEXT_TYPES.map(ctx => (
                <SelectItem key={ctx.value} value={ctx.value}>
                  <div className="flex items-center gap-2">
                    <ctx.icon className="h-4 w-4" />
                    <span>{ctx.label}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-12 gap-6">
          {/* Sessions sidebar */}
          <div className="col-span-3">
            <Card className={isDarkMode ? 'bg-card border-border' : ''}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium">Conversations</CardTitle>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={createNewSession}
                    title="New chat"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-2">
                <ScrollArea className="h-[500px]">
                  {loadingSessions ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                    </div>
                  ) : sessions.length === 0 ? (
                    <div className="text-center py-8">
                      <MessageSquare className={`h-8 w-8 mx-auto mb-2 ${isDarkMode ? 'text-gray-600' : 'text-gray-300'}`} />
                      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        No conversations yet
                      </p>
                      <Button
                        variant="link"
                        className="mt-2"
                        onClick={createNewSession}
                      >
                        Start a new chat
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {sessions.map((session) => (
                        <div
                          key={session.session_id}
                          className={`group flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                            currentSessionId === session.session_id
                              ? isDarkMode
                                ? 'bg-purple-900/30 border border-purple-700'
                                : 'bg-purple-50 border border-purple-200'
                              : isDarkMode
                                ? 'hover:bg-gray-800'
                                : 'hover:bg-gray-100'
                          }`}
                          onClick={() => setCurrentSessionId(session.session_id)}
                        >
                          <MessageSquare className={`h-4 w-4 flex-shrink-0 ${
                            isDarkMode ? 'text-gray-400' : 'text-gray-500'
                          }`} />
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm truncate ${
                              isDarkMode ? 'text-gray-200' : 'text-gray-700'
                            }`}>
                              {session.last_message}
                            </p>
                            <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                              {new Date(session.last_updated).toLocaleDateString()}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 opacity-0 group-hover:opacity-100"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteSession(session.session_id);
                            }}
                          >
                            <Trash2 className="h-3 w-3 text-red-500" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>

          {/* Chat area */}
          <div className="col-span-9">
            <Card className={`h-[600px] flex flex-col ${isDarkMode ? 'bg-card border-border' : ''}`}>
              <CardContent className="flex-1 flex flex-col p-0">
                {/* Messages */}
                <ScrollArea className="flex-1 p-4">
                  {messages.length === 0 ? (
                    <div className="h-full flex flex-col items-center justify-center text-center">
                      <Sparkles className={`h-16 w-16 mb-4 ${
                        isDarkMode ? 'text-purple-400' : 'text-purple-500'
                      }`} />
                      <h3 className={`text-xl font-semibold mb-2 ${
                        isDarkMode ? 'text-white' : 'text-gray-900'
                      }`}>
                        Hi {user?.full_name?.split(' ')[0] || 'there'}!
                      </h3>
                      <p className={`text-sm mb-6 max-w-md ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        I'm your AI assistant. I can help you with CRM queries, 
                        sales coaching, and data insights. What would you like to know?
                      </p>
                      
                      {/* Quick suggestions */}
                      <div className="grid grid-cols-2 gap-2 max-w-lg">
                        {suggestions.map((suggestion, idx) => (
                          <button
                            key={idx}
                            onClick={() => sendMessage(suggestion)}
                            className={`text-left px-4 py-3 rounded-lg text-sm transition-colors ${
                              isDarkMode 
                                ? 'bg-gray-800 hover:bg-gray-700 text-gray-300 border border-gray-700' 
                                : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                            }`}
                          >
                            {suggestion}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {messages.map((msg, idx) => (
                        <div
                          key={idx}
                          className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-[75%] rounded-2xl px-4 py-3 ${
                              msg.role === 'user'
                                ? isDarkMode 
                                  ? 'bg-purple-600 text-white' 
                                  : 'bg-purple-500 text-white'
                                : isDarkMode
                                  ? 'bg-gray-800 text-gray-100 border border-gray-700'
                                  : 'bg-gray-100 text-gray-900'
                            }`}
                          >
                            <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                            {msg.created_at && (
                              <p className={`text-xs mt-1 ${
                                msg.role === 'user' 
                                  ? 'text-purple-200' 
                                  : isDarkMode ? 'text-gray-500' : 'text-gray-400'
                              }`}>
                                {new Date(msg.created_at).toLocaleTimeString()}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                      
                      {isLoading && (
                        <div className="flex justify-start">
                          <div className={`rounded-2xl px-4 py-3 ${
                            isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-gray-100'
                          }`}>
                            <div className="flex items-center gap-2">
                              <Loader2 className={`w-4 h-4 animate-spin ${
                                isDarkMode ? 'text-purple-400' : 'text-purple-500'
                              }`} />
                              <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                Thinking...
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                      <div ref={messagesEndRef} />
                    </div>
                  )}
                </ScrollArea>

                {/* Input */}
                <div className={`p-4 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                  <div className="flex items-center gap-2">
                    <VoiceMessageRecorder 
                      compact 
                      chatType="ai_chat"
                      onSend={(msg) => {
                        // Handle voice message (could transcribe or just acknowledge)
                        toast.info('Voice messages coming soon!');
                      }}
                    />
                    <Input
                      ref={inputRef}
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Ask me anything about your CRM..."
                      disabled={isLoading}
                      className={`flex-1 ${isDarkMode ? 'bg-gray-800 border-gray-700' : ''}`}
                      data-testid="ai-chat-page-input"
                    />
                    <Button
                      onClick={() => sendMessage(inputValue)}
                      disabled={!inputValue.trim() || isLoading}
                      className={isDarkMode ? 'bg-purple-600 hover:bg-purple-500' : 'bg-purple-500 hover:bg-purple-600'}
                      data-testid="ai-chat-page-send"
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIChatAssistant;
